import * as THREE from "three";
import { DRACOLoader } from "three/addons/loaders/DRACOLoader.js";
import { GLTFLoader } from "three/addons/loaders/GLTFLoader.js";
import { STLLoader } from "three/addons/loaders/STLLoader.js";
import { STLExporter } from "three/addons/exporters/STLExporter.js";
import * as BufferGeometryUtils from "three/addons/utils/BufferGeometryUtils.js";

function getGeometryFromStl(arrayBuffer, callback) {
  let stlLoader = new STLLoader();
  const geometry = stlLoader.parse(arrayBuffer);
  if (callback) callback(geometry);
  return geometry;
}

async function getGeometryFromGlb(arrayBuffer, callback) {
  let modelGeometry = null;
  try {
    let gltfLoader = new GLTFLoader();
    let result = await gltfLoader.parseAsync(arrayBuffer, "");
    let world = result.scene.children[0];
    let geometries = [];
    world.children.forEach((element) => {
      if (element.type === "Mesh") {
        geometries.push(element.geometry);
      }
    });
    modelGeometry =
      geometries.length == 1 ? geometries[0] : getGMergedGeometry(geometries);
    if (callback) callback(modelGeometry);
  } catch (error) {
    console.log("getGeometryFromGlb ERROR", error);
  }

  return modelGeometry;
}

function getGeometryFromDrc(arrayBuffer, callback) {
  // <script src="https://www.gstatic.com/draco/versioned/decoders/1.4.1/draco_wasm_wrapper.js"></script>
  let dracoLoader = new DRACOLoader();
  dracoLoader.setDecoderPath(
    "https://www.gstatic.com/draco/versioned/decoders/1.4.1/"
  );

  dracoLoader.parse(arrayBuffer, callback);
}

function getMergedGeometry(meshes) {
  let modelGeometry = null;
  if (meshes && meshes.length) {
    let geometries = [];
    meshes.forEach((b) => {
      let clone = b.clone();
      clone.updateMatrixWorld();

      let geometry = clone.geometry.clone().applyMatrix4(clone.matrixWorld);
      if (geometry.index) geometry = geometry.toNonIndexed();
      if (geometry.attributes) {
        geometry.deleteAttribute("color");
        geometry.deleteAttribute("uv");
        // geometry.deleteAttribute("normal");
        // geometry.computeVertexNormals();
        // geometry.computeBoundingBox();
        geometry.attributes.position.gpuType = 1015;
        geometry.attributes.normal.gpuType = 1015;
      }

      geometries.push(geometry);
    });

    modelGeometry = BufferGeometryUtils.mergeGeometries(geometries, true);
    modelGeometry.deleteAttribute("normal");
    modelGeometry = BufferGeometryUtils.mergeVertices(modelGeometry); // , 1
    modelGeometry.computeVertexNormals();
  }
  return modelGeometry;
}

function getGMergedGeometry(mgeometries) {
  let modelGeometry = null;
  if (mgeometries && mgeometries.length) {
    let geometries = [];
    mgeometries.forEach((mgeometry) => {
      let geometry = mgeometry.clone(); //.applyMatrix4(clone.matrixWorld);
      if (geometry.index) geometry = geometry.toNonIndexed();
      if (geometry.attributes) {
        geometry.deleteAttribute("color");
        geometry.deleteAttribute("uv");
        // geometry.deleteAttribute("normal");
        // geometry.computeVertexNormals();
        // geometry.computeBoundingBox();
        if (geometry.attributes.position)
          geometry.attributes.position.gpuType = 1015;
        if (geometry.attributes.normal)
          geometry.attributes.normal.gpuType = 1015;
      }
      geometries.push(geometry);
    });

    modelGeometry = BufferGeometryUtils.mergeGeometries(geometries, true);
    modelGeometry.deleteAttribute("normal");
    modelGeometry = BufferGeometryUtils.mergeVertices(modelGeometry); // , 1
    modelGeometry.computeVertexNormals();
  }
  return modelGeometry;
}

function exportMeshToStlBlob(mesh) {
  const stlExporter = new STLExporter();
  // const data = stlExporter.parse(mesh);
  // const blob = new Blob([data], { type: 'text/plain' });
  const data = stlExporter.parse(mesh, { binary: true });
  const blob = new Blob([data], { type: "application/octet-stream" });
  return blob;
}

function getFaces(geometry, needColor) {
  const faces = [];
  const position = geometry.getAttribute("position");
  const colorAttr = geometry.getAttribute("color");

  const index = geometry.index; //mesh.geometry.getIndex();
  if (index != null) {
    for (let i = 0; i < index.count; i += 3) {
      const face = {
        a: index.getX(i),
        b: index.getX(i + 1),
        c: index.getX(i + 2),
      };
      face.indexes = [face.a, face.b, face.c];
      faces.push(face);
    }
  } else {
    for (let i = 0; i < position.count; i += 3) {
      const face = {
        a: i,
        b: i + 1,
        c: i + 2,
      };
      face.indexes = [face.a, face.b, face.c];
      faces.push(face);
    }
  }

  for (let j = 0; j < faces.length; j++) {
    let face = faces[j];
    face.pointA = new THREE.Vector3(
      position.getX(face.a),
      position.getY(face.a),
      position.getZ(face.a)
    );
    face.pointB = new THREE.Vector3(
      position.getX(face.b),
      position.getY(face.b),
      position.getZ(face.b)
    );
    face.pointC = new THREE.Vector3(
      position.getX(face.c),
      position.getY(face.c),
      position.getZ(face.c)
    );

    if (colorAttr && needColor) {
      face.colorA = {
        r: colorAttr.getX(face.a),
        g: colorAttr.getY(face.a),
        b: colorAttr.getZ(face.a),
        a: colorAttr.getW(face.a),
      };
      face.colorB = {
        r: colorAttr.getX(face.b),
        g: colorAttr.getY(face.b),
        b: colorAttr.getZ(face.b),
        a: colorAttr.getW(face.b),
      };
      face.colorC = {
        r: colorAttr.getX(face.c),
        g: colorAttr.getY(face.c),
        b: colorAttr.getZ(face.c),
        a: colorAttr.getW(face.c),
      };
    }

    face.pointA.index = face.a;
    face.pointB.index = face.b;
    face.pointC.index = face.c;
    face.points = [face.pointA, face.pointB, face.pointC];

    face.normal = new THREE.Vector3();
    let faceTriangle = new THREE.Triangle(
      face.pointA,
      face.pointB,
      face.pointC
    );
    faceTriangle.getNormal(face.normal);
    face.triangle = faceTriangle;
    face.area = faceTriangle.getArea();
  }

  return faces;
}

function getFacesArea(geometry) {
  let facesArea = 0;
  try {
    let faces = getFaces(geometry);
    if (faces && faces.length)
      for (const face of faces) {
        facesArea += face.area;
      }
  } catch (error) {
    console.log("getFacesArea ERROR", error);
  }

  return facesArea;
}

function isColorGrey(color) {
  return color && color.r === 0.4 && color.g === 0.4 && color.b === 0.4;
}

export {
  getGeometryFromStl,
  getGeometryFromGlb,
  getGeometryFromDrc,
  getMergedGeometry,
  exportMeshToStlBlob,
  getFaces,
  getFacesArea
};
